import { Link } from "react-router-dom";
import { Button } from "antd";
import { motion } from "motion/react";
import Services from "./Services";

const ease = [0.22, 1, 0.36, 1];

const services = [
  {
    icon: "🌿",
    title: "Shamana Therapy",
    desc: "A gentle Ayurvedic treatment that balances doshas using herbal medicines, diet, and lifestyle changes without detoxification.",
  },
  {
    icon: "🪔",
    title: "Panchakarma",
    desc: "A complete Ayurvedic detoxification and rejuvenation therapy that removes toxins and restores the body's natural balance.",
  },
  {
    icon: "💧",
    title: "Uttar Basti",
    desc: "A specialized Ayurvedic therapy for urinary and reproductive health using medicated oils or herbal decoctions.",
  },
  {
    icon: "🍃",
    title: "Virechana",
    desc: "A therapeutic purgation procedure that cleanses excess Pitta toxins and supports liver, skin, and digestive health.",
  },
  {
    icon: "🫗",
    title: "Basti",
    desc: "A medicated enema therapy considered the best treatment for Vata disorders, improving joint, nerve, and digestive health.",
  },
  {
    icon: "👃",
    title: "Nasya",
    desc: "A nasal cleansing therapy using herbal oils to improve sinus health, breathing, headaches, and mental clarity.",
  },
  {
    icon: "🌸",
    title: "Yoniprakshalana",
    desc: "An Ayurvedic vaginal cleansing therapy that promotes feminine hygiene and helps manage gynecological conditions.",
  },
  {
    icon: "🌺",
    title: "Uttarbasti",
    desc: "A therapeutic procedure using medicated oils for infertility, urinary disorders, and reproductive wellness.",
  },
];

// Cycled per-card accent so the grid has rhythm without being noisy
const accents = [
  { ring: "from-[#e8c580] to-brand-gold", glow: "shadow-[#c99348]/25" },
  { ring: "from-[#e2a58c] to-[#c1694f]", glow: "shadow-[#c1694f]/25" },
  { ring: "from-[#b7cdb4] to-[#7f9c7c]", glow: "shadow-[#7f9c7c]/25" },
];

const journeySteps = [
  {
    n: "01",
    title: "Consultation",
    desc: "Share your health history and current concerns with our Ayurvedic physicians.",
  },
  {
    n: "02",
    title: "Prakriti Assessment",
    desc: "We identify your unique body constitution and the root of your dosha imbalance.",
  },
  {
    n: "03",
    title: "Personalized Treatment",
    desc: "A tailored plan drawing on the therapies above, sequenced for your condition.",
  },
  {
    n: "04",
    title: "Ongoing Care",
    desc: "Follow-up sessions and lifestyle guidance so the results actually last.",
  },
];

export default function Treatments() {
  return (
    <section className="relative py-24 bg-[#faf6ee] overflow-hidden">
      {/* faint decorative backdrop */}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <Services />
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease }}
          className="max-w-2xl mx-auto text-center mb-16"
        >
          <p className="uppercase md:text-xl text-base font-extrabold text-brand-gold mb-4 ">
            Featured Treatments
          </p>
          <h2 className="text-4xl sm:text-5xl font-bold text-brand-forest leading-tight mb-5">
            Discover Our Dedicated Ayurvedic Treatments
          </h2>
          <p className="text-gray-600 leading-8">
            Comprehensive wellness solutions tailored to your unique body constitution
            (Prakriti) and specific health needs.
          </p>
        </motion.div>

        {/* Cards */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {services.map((service, idx) => {
            const accent = accents[idx % accents.length];
            return (
              <motion.div
                key={idx}
                variants={{
                  hidden: { opacity: 0, y: 30, scale: 0.96 },
                  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease } },
                }}
                whileHover={{ y: -8 }}
                className="group relative bg-white rounded-[28px] border border-[#efe6d3] shadow-md hover:shadow-xl transition-shadow duration-500 p-7 flex flex-col overflow-hidden"
              >
                {/* top accent line, draws in on hover */}
                <div
                  className={`absolute top-0 left-0 h-[3px] w-full bg-gradient-to-r ${accent.ring} scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500`}
                />

                {/* medallion icon */}
                <div className="relative w-16 h-16 mb-6">
                  <div
                    className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${accent.ring} opacity-15 group-hover:opacity-25 transition-opacity duration-500 blur-md`}
                  />
                  <div className="relative w-16 h-16 rounded-2xl bg-[#faf6ee] border border-[#efe6d3] flex items-center justify-center text-3xl group-hover:scale-110 group-hover:-rotate-6 transition-transform duration-500">
                    {service.icon}
                  </div>
                </div>

                <h3 className="text-lg font-bold text-brand-forest mb-2.5">{service.title}</h3>
                <p className="text-gray-600 text-sm leading-6 flex-1">{service.desc}</p>

                <div className="mt-5 flex items-center gap-1.5 text-xs font-semibold text-brand-gold opacity-0 group-hover:opacity-100 translate-x-[-6px] group-hover:translate-x-0 transition-all duration-400">
                  Learn more
                  <span aria-hidden>→</span>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* ---- Your Path to Wellness ---- */}
      <div className="relative max-w-6xl mx-auto px-6 lg:px-12 mt-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease }}
          className="max-w-xl mx-auto text-center mb-16"
        >
          <p className="uppercase md:text-xl text-base font-extrabold text-brand-gold mb-4">
            How It Works
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-brand-forest leading-tight">
            Your Path to Wellness
          </h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.15 } } }}
          className="relative grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6"
        >
          {/* connecting line, desktop only */}
          <div className="hidden md:block absolute top-7 left-[12.5%] right-[12.5%] h-px bg-[#e2d5b8]" />

          {journeySteps.map((step, idx) => (
            <motion.div
              key={idx}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease } },
              }}
              className="relative flex flex-col items-center text-center"
            >
              <div className="relative z-10 w-14 h-14 rounded-full bg-white border-2 border-brand-gold flex items-center justify-center font-bold text-brand-forest mb-5">
                {step.n}
              </div>
              <h3 className="text-base font-bold text-brand-forest mb-2">{step.title}</h3>
              <p className="text-gray-600 text-sm leading-6 max-w-[220px]">{step.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="flex justify-center mt-16"
        >
          <Link to="/appointment">
            <Button
              type="primary"
              shape="round"
              size="large"
              className="!bg-brand-forest hover:!bg-brand-gold !border-none !h-12 !px-8 !font-semibold"
            >
              Book Your Consultation →
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}